import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, CircularProgress, Divider, Paper, Stack } from '@mui/material';
import { datadogRum } from '@datadog/browser-rum';

import StatementUploadGroupRow from './StatementUploadGroupRow';

import TaskGroupDrawer from 'modules/Tasks/Groups/TaskGroupDrawer';
import UnusedAccountsHint from 'modules/Tasks/Groups/customComponents/UnusedAccountsHint';
import StatementUploadModal from 'modules/common/components/StatementUpload/StatementUploadModal';
import { GROUP_PANEL_TASK_STATUSES, Task, TaskGroupSummary, TaskStatuses } from 'modules/Tasks/types';
import useMemberTasks from 'services/memberTasks/queries/useMemberTasks';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';
import useStatementUploadResolvability, {
    TaskResolvability,
} from 'modules/common/components/StatementUpload/hooks/useStatementUploadResolvability';
import { useDocumentFiltered } from 'modules/common/queries/documents';
import useUpdateOperatorTaskStatus from 'services/operatorTasks/mutations/useUpdateOperatorTaskStatus';
import { TaskStatus } from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import {
    StatementUploadTaskContext,
    getInstitutionReason,
} from 'modules/common/components/StatementUpload/StatementUpload.interface';
import { Document } from 'modules/common/interfaces/documents';

interface MonthlyStatementUploadGroupProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly group: TaskGroupSummary;
}

/**
 * Whether a task's context is a statement-upload context. Custom group components must not assume
 * child-task shape — a group template could carry tasks of other types (or dev/seed data), and this
 * guard keeps us from crashing on them.
 */
function isStatementUploadContext(context: unknown): context is StatementUploadTaskContext {
    return (
        typeof context === 'object' &&
        context !== null &&
        Array.isArray((context as StatementUploadTaskContext).accounts) &&
        'institution_name' in context
    );
}

/** Count of accounts on a task with no uploaded statement yet (matched by filename prefix). */
function countStatementsNeeded(context: StatementUploadTaskContext, documents: Document[]): number {
    return context.accounts.filter((account) => {
        const entryYear = account.year ?? context.year;
        const entryMonth = account.month ?? context.month;
        const prefix = `${entryYear}-${entryMonth}-${account.plaid_institution_id ?? ''}-${account.account_mask}-`;
        return !documents.some((doc) => doc.file_name.startsWith(prefix));
    }).length;
}

interface GroupBodyProps {
    readonly tasks: Task[];
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly documents: Document[];
    readonly resolvabilityMap: Record<string, TaskResolvability>;
    readonly completedTaskIds: Set<string>;
    readonly onUploadClick: (task: Task) => void;
    readonly onReconnectSuccess: (taskId: string) => void;
}

const FALLBACK_RESOLVABILITY: TaskResolvability = {
    isResolvable: false,
    connectorId: null,
    connectionId: null,
    isConnectionLost: false,
    offerAutoStatements: true,
};

function GroupBody({
    tasks,
    isLoading,
    isError,
    documents,
    resolvabilityMap,
    completedTaskIds,
    onUploadClick,
    onReconnectSuccess,
}: Readonly<GroupBodyProps>) {
    if (isLoading) {
        return (
            <Stack sx={{ alignItems: 'center', py: 4 }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (isError) {
        return <Alert severity="error">We couldn&apos;t load these tasks. Please try again.</Alert>;
    }

    if (tasks.length === 0) {
        return null;
    }

    return (
        <Stack sx={{ gap: 4 }}>
            <Paper variant="outlined">
                <Stack divider={<Divider />}>
                    {tasks.map((task) => {
                        const context = task.context as unknown as StatementUploadTaskContext;
                        return (
                            <StatementUploadGroupRow
                                key={task.id}
                                institutionName={context.institution_name}
                                accounts={context.accounts}
                                plaidInstitutionId={context.accounts[0]?.plaid_institution_id}
                                reason={getInstitutionReason(context.accounts)}
                                resolvability={resolvabilityMap[task.id] ?? FALLBACK_RESOLVABILITY}
                                statementsNeededCount={countStatementsNeeded(context, documents)}
                                isCompleted={completedTaskIds.has(task.id) || task.status === TaskStatuses.COMPLETED}
                                taskId={task.id}
                                year={context.year}
                                month={context.month}
                                onUploadClick={() => onUploadClick(task)}
                                onReconnectSuccess={() => onReconnectSuccess(task.id)}
                            />
                        );
                    })}
                </Stack>
            </Paper>
            <UnusedAccountsHint />
        </Stack>
    );
}

/**
 * Custom group flow for the `monthly_bank_statements` task group. Reuses the standard group drawer
 * shell and renders one institution row per child `upload_missing_bank_statement` task, driving the
 * same client-orchestrated completion the legacy StatementUpload flow used — the backend only knows
 * READY → COMPLETED; this component decides when to POST it (manual submit, Quiltt enable/repair, or
 * auto-complete when all accounts are already Quiltt-enabled).
 */
export default function MonthlyStatementUploadGroup({
    open,
    onClose,
    group,
}: Readonly<MonthlyStatementUploadGroupProps>) {
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();
    // Only fetch once the panel is open — like the default expand flow, whose GroupTaskList mounts
    // (and fetches) only inside the open drawer. Keeps closed group cards from firing a query each.
    const {
        data: allTasks = [],
        isLoading,
        isError,
    } = useMemberTasks({ groupId: group.id, statuses: GROUP_PANEL_TASK_STATUSES }, { enabled: open });

    // Guard against non-statement child tasks so we never crash on unexpected context shapes.
    const tasks = useMemo(() => allTasks.filter((task) => isStatementUploadContext(task.context)), [allTasks]);

    const {
        resolvabilityMap,
        resolvabilityCheckers,
        isLoading: isResolvabilityLoading,
    } = useStatementUploadResolvability(tasks);
    const {
        data: documents = [],
        isLoading: isDocumentsLoading,
        isError: isDocumentsError,
    } = useDocumentFiltered({ document_type: 'BUSINESS_BANK_STATEMENT' }, { enabled: open });
    const updateTaskStatus = useUpdateOperatorTaskStatus();

    const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
    const [activeTask, setActiveTask] = useState<Task | null>(null);

    const handleTaskCompleted = useCallback(
        (taskId: string) => {
            setCompletedTaskIds((prev) => new Set(prev).add(taskId));
            // Grouped completion: the helper refetches only the task list, so the row flips to its
            // completed state while the group card and priority feed stay put until the drawer
            // closes (useGroupAction.close). It owns that branch so a completion path cannot miss it.
            invalidateMemberTaskQueries({ group_id: group.id });
        },
        [invalidateMemberTaskQueries, group.id]
    );

    const handleMarkComplete = useCallback(
        (taskId: string) => {
            updateTaskStatus.mutate(
                { id: taskId, status: TaskStatus.COMPLETED },
                {
                    onSuccess: () => handleTaskCompleted(taskId),
                    onError: (error) => {
                        datadogRum.addError(error instanceof Error ? error : new Error(String(error)), {
                            source: 'MonthlyStatementUploadGroup.handleMarkComplete',
                            taskId,
                        });
                    },
                }
            );
        },
        [updateTaskStatus, handleTaskCompleted]
    );

    // Auto-complete tasks whose accounts already have Quiltt auto-statements enabled (fires once each).
    const autoCompletedRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        if (isResolvabilityLoading) {
            return;
        }
        tasks.forEach((task) => {
            if (
                task.status === TaskStatuses.COMPLETED ||
                completedTaskIds.has(task.id) ||
                autoCompletedRef.current.has(task.id)
            ) {
                return;
            }
            const context = task.context as unknown as StatementUploadTaskContext;
            const allAutoEnabled =
                context.accounts.length > 0 && context.accounts.every((a) => a.is_quiltt && !!a.quiltt_account_id);
            if (allAutoEnabled) {
                handleMarkComplete(task.id);
                autoCompletedRef.current.add(task.id);
            }
        });
    }, [tasks, isResolvabilityLoading, completedTaskIds, handleMarkComplete]);

    const handleUploadClick = useCallback((task: Task) => setActiveTask(task), []);
    const handleModalClose = useCallback(() => setActiveTask(null), []);
    // Clear any in-progress upload selection when the drawer closes so a stale activeTask can't
    // re-surface the modal on the next open.
    const handleDrawerClose = useCallback(() => {
        setActiveTask(null);
        onClose();
    }, [onClose]);

    const activeTaskContext = activeTask ? (activeTask.context as unknown as StatementUploadTaskContext) : null;
    const activeTaskResolvability = activeTask ? resolvabilityMap[activeTask.id] : null;

    return (
        <>
            <TaskGroupDrawer group={group} open={open} onClose={handleDrawerClose}>
                {resolvabilityCheckers}
                <GroupBody
                    tasks={tasks}
                    isLoading={isLoading || isResolvabilityLoading || isDocumentsLoading}
                    isError={isError || isDocumentsError}
                    documents={documents}
                    resolvabilityMap={resolvabilityMap}
                    completedTaskIds={completedTaskIds}
                    onUploadClick={handleUploadClick}
                    onReconnectSuccess={handleMarkComplete}
                />
            </TaskGroupDrawer>

            {open && activeTask && activeTaskContext && (
                <StatementUploadModal
                    open
                    onClose={handleModalClose}
                    taskId={activeTask.id}
                    context={activeTaskContext}
                    documents={documents}
                    isResolvable={activeTaskResolvability?.isResolvable}
                    connectorId={activeTaskResolvability?.connectorId}
                    connectionId={activeTaskResolvability?.connectionId}
                    isConnectionLost={activeTaskResolvability?.isConnectionLost}
                    onTaskCompleted={handleTaskCompleted}
                />
            )}
        </>
    );
}
